import {
  ContentWrapper,
  FloatingFormItem,
  InputSearch,
  PlanAccountsSelect,
} from '@/components/_common';
import AntdFeatherIcon from '@/components/_common/AntdFeatherIcon';
import CompaniesInfiniteSelectField from '@/components/_common/CompaniesInfiniteSelectField';
import CostCenterInfiniteSelectField from '@/components/_common/CostCenterInfiniteSelectField';
import EmployeesSelectedModal from '@/components/_common/EmployeesSelectedModal';
import SuppliersSelectedModal from '@/components/_common/SuppliersSelectedModal';
import SuppliersCreateModal from '@/components/Suppliers/SuppliersCreateModal';
import { Employee } from '@/core/domain/enterprise/Employees/Employees.types';
import {
  PlanAccountDestination,
  PlanAccountHierarchy,
} from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.types';
import {
  AccountPayableReleaseFormValues,
  Creditor,
  Installment,
  InstallmentSituation,
} from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import { SupplierReduced } from '@/core/domain/people/Suppliers/Suppliers.types';
import { useDefaultEmptyFieldError, useDisclosure } from '@/hooks';
import { useAccountPayableContext } from '@/pages/FinancialPages/BillToPayPage/ReleasesPage/ReleasesDetailsPage';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { Col, Form, FormInstance, Row, Select } from 'antd';
import React, { useMemo } from 'react';
import { FaPlus } from 'react-icons/fa';
import { useIntl } from 'react-intl';

interface Props {
  form: FormInstance<AccountPayableReleaseFormValues>;
  installment?: Installment;
}
const CardAccountPayable: React.FC<Props> = ({ form, installment }) => {
  const intl = useIntl();
  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const [modalEmployeeVisible, modalEmployeeAction] = useDisclosure();
  const [modalSupplierVisible, modalSupplierAction] = useDisclosure();
  const [supplierCreateVisible, supplierCreateAction] = useDisclosure();

  const { accountPayable } = useAccountPayableContext();

  const handleSupplierSelect = (supplier?: SupplierReduced) => {
    form.setFieldsValue({
      supplier: supplier?.name,
      supplierId: supplier?.id,
    });
    modalSupplierAction.close();
  };

  const handleEmployeeSelect = (employee?: Employee) => {
    form.setFieldsValue({
      employee: employee?.name,
      employeeId: employee?.id,
    });
    modalEmployeeAction.close();
  };

  const disabledBySituation =
    installment?.situation === InstallmentSituation.settled ||
    installment?.situation === InstallmentSituation.canceled;

  const hasInstallmentsSettled = useMemo(
    () =>
      accountPayable?.installmentPayables.some(
        (installment) => installment.situation !== InstallmentSituation.open,
      ),
    [accountPayable],
  );

  return (
    <>
      <ContentWrapper
        style={{
          width: '100%',
          marginBottom: 20,
          padding: '1.5rem 2rem 1.25rem',
        }}
      >
        <Row gutter={[16, 9]}>
          <Col span={24} xl={10}>
            <FloatingFormItem name="companyId" label="Empresa">
              <CompaniesInfiniteSelectField
                disabled={hasInstallmentsSettled || disabledBySituation}
              />
            </FloatingFormItem>
          </Col>

          <Col sm={24} xs={24} md={6} xl={4}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'release.form.creditor',
              })}
              name="creditor"
              rules={[defaultEmptyFieldError]}
              initialValue={Creditor.supplier}
            >
              <Select
                allowClear
                style={{ width: '100' }}
                options={[
                  {
                    value: Creditor.supplier,
                    label: intl.formatMessage({
                      id: 'release.form.supplier',
                    }),
                  },
                  {
                    value: Creditor.employee,
                    label: intl.formatMessage({
                      id: 'release.form.employee',
                    }),
                  },
                ]}
                disabled={
                  hasInstallmentsSettled ||
                  disabledBySituation ||
                  accountPayable?.purchase
                }
              />
            </FloatingFormItem>
          </Col>

          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const creditor = getFieldValue('creditor');

              if (creditor === Creditor.employee) {
                return (
                  <Col sm={24} xs={24} md={18} xl={10}>
                    <Form.Item hidden name="employeeId" />

                    <FloatingFormItem
                      name="employee"
                      label={intl.formatMessage({
                        id: 'release.form.employee',
                      })}
                      rules={[defaultEmptyFieldError]}
                    >
                      <InputSearch
                        inputProps={{ disabled: true }}
                        buttonProps={{
                          onClick: modalEmployeeAction.open,
                          disabled:
                            hasInstallmentsSettled || disabledBySituation,
                        }}
                      />
                    </FloatingFormItem>
                  </Col>
                );
              } else {
                return (
                  <Col sm={24} xs={24} md={18} xl={10}>
                    <Form.Item hidden name="supplierId" />

                    <FloatingFormItem
                      name="supplier"
                      label={intl.formatMessage({
                        id: 'release.form.supplier',
                      })}
                      rules={[defaultEmptyFieldError]}
                    >
                      <InputSearch
                        inputProps={{ disabled: true }}
                        buttonProps={{
                          onClick: modalSupplierAction.open,
                          disabled:
                            hasInstallmentsSettled ||
                            disabledBySituation ||
                            accountPayable?.purchase,
                        }}
                        secondaryButtonProps={
                          !(
                            hasInstallmentsSettled ||
                            disabledBySituation ||
                            accountPayable?.purchase
                          )
                            ? {
                                onClick: supplierCreateAction.open,
                                title: !hasPermissions([
                                  Authorities.REGISTER_SUPPLIER_ADD,
                                ])
                                  ? 'Você não tem permissão para cadastrar um fornecedor'
                                  : 'Novo fornecedor',
                                icon: (
                                  <AntdFeatherIcon
                                    icon={<FaPlus />}
                                    size={16}
                                  />
                                ),
                                disabled: !hasPermissions([
                                  Authorities.REGISTER_SUPPLIER_ADD,
                                ]),
                              }
                            : undefined
                        }
                      />
                    </FloatingFormItem>
                  </Col>
                );
              }
            }}
          </Form.Item>

          <Col sm={24} xs={24} md={12} xl={12}>
            <FloatingFormItem
              name="planAccountId"
              label={intl.formatMessage({
                id: 'release.form.planAccount',
              })}
            >
              <PlanAccountsSelect
                queryFilter={{
                  hierarchy: PlanAccountHierarchy.Analitical,
                  destination: PlanAccountDestination.Expense,
                  active: true,
                }}
                allowClear
                disabled={hasInstallmentsSettled || disabledBySituation}
              />
            </FloatingFormItem>
          </Col>

          <Col sm={24} xs={24} md={12} xl={12}>
            <FloatingFormItem
              name={'costCenterId'}
              label={intl.formatMessage({
                id: 'release.form.costCenter',
              })}
            >
              <CostCenterInfiniteSelectField
                disabled={hasInstallmentsSettled || disabledBySituation}
                allowClear
              />
            </FloatingFormItem>
          </Col>
        </Row>
      </ContentWrapper>

      <EmployeesSelectedModal
        visible={modalEmployeeVisible}
        handleVisible={modalEmployeeAction.toggle}
        noSelection
        onEmployeeSelect={handleEmployeeSelect}
      />

      <SuppliersSelectedModal
        visible={modalSupplierVisible}
        handleVisible={modalSupplierAction.toggle}
        noSelection
        onSupplierSelect={handleSupplierSelect}
      />

      <SuppliersCreateModal
        open={supplierCreateVisible}
        onClose={supplierCreateAction.close}
        onSuccess={handleSupplierSelect}
      />
    </>
  );
};

export default CardAccountPayable;
